import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Dices, Loader2, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { explain } from '@/shared/api/problem'
import { ErpTable, Td, Th } from '@/components/erp/primitives'
import { EmptyState, ErrorStrip, LoadingRows, Note } from '@/shared/ui/states'
import { Modal } from '@/shared/ui/Modal'
import { fmtNumber } from '@/shared/lib/format'
import { useElectionContext } from '@/shared/election/context'
import { NoElectionSelected } from '@/shared/election/ElectionSelector'
import { EntityPicker } from '@/shared/pickers/EntityPicker'
import {
  clearAspirant, drawPositions, listAspirants, listNominations, recordFee, registerAspirant,
  rejectAspirant, startVetting, withdrawAspirant, type Aspirant, type Nomination,
} from './api'
import { AspirantStatusChip, seatLabel } from './vocab'

/**
 * Who is standing, and whether they are cleared to.
 *
 * ## Ballot position is drawn, never assigned
 *
 * The first name on a ballot gets more votes than the last — in every election ever studied.
 * Assigning positions in application order would hand that advantage to whoever queued
 * earliest, so they are drawn at random once vetting closes. The server records the seed in
 * the audit trail, which is what makes "prove it was random" answerable afterwards. There is
 * deliberately no way to choose the seed.
 */
export function AspirantsPage() {
  const { electionId } = useElectionContext()
  const [params, setParams] = useSearchParams()
  const nominationId = params.get('nomination')

  const [contests, setContests] = useState<Nomination[]>([])
  const [rows, setRows] = useState<Aspirant[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)

  useEffect(() => {
    if (!electionId) return
    void (async () => {
      try {
        setContests(await listNominations({ electionId, size: 300 }))
      } catch (err) {
        setError(explain(err))
      }
    })()
  }, [electionId])

  const load = useCallback(async () => {
    if (!nominationId) { setRows(null); return }
    try {
      setError(null)
      setRows(await listAspirants(nominationId))
    } catch (err) {
      setError(explain(err))
      setRows([])
    }
  }, [nominationId])

  useEffect(() => { void load() }, [load])

  if (!electionId) return <NoElectionSelected what="An aspirant list" />

  const act = async (id: string, what: string) => {
    if (!nominationId) return
    setBusyId(id)
    setError(null)
    try {
      if (what === 'vet') await startVetting(nominationId, id)
      else if (what === 'clear') await clearAspirant(nominationId, id)
      else if (what === 'withdraw') await withdrawAspirant(nominationId, id)
      else if (what === 'reject') {
        const reason = window.prompt('Reason for rejection (recorded permanently):')
        if (!reason) return
        await rejectAspirant(nominationId, id, reason)
      } else if (what === 'fee') {
        const ref = window.prompt('Payment reference (M-Pesa receipt):')
        if (!ref) return
        await recordFee(nominationId, id, ref)
      }
      await load()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusyId(null)
    }
  }

  const draw = async () => {
    if (!nominationId) return
    if (!window.confirm(
      'This draws ballot positions at random for every cleared aspirant and replaces any '
      + 'existing order. The seed is recorded so the draw can be reproduced. Continue?')) return
    setBusyId('draw')
    try {
      setError(null)
      await drawPositions(nominationId)
      await load()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-erp-base font-semibold">Aspirants</h2>
          <p className="text-erp-xs text-muted-foreground mt-0.5">
            Applications, fees, vetting, and the ballot draw.
          </p>
        </div>
        {nominationId && (
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => void draw()}
              className="erp-btn h-9 px-3 text-erp-sm inline-flex items-center gap-2">
              {busyId === 'draw' ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <Dices className="h-3.5 w-3.5" />}
              Draw positions
            </button>
            <button type="button" onClick={() => setAdding(true)}
              className="erp-btn-primary h-9 px-3 text-erp-sm inline-flex items-center gap-2">
              <Plus className="h-3.5 w-3.5" /> Register an aspirant
            </button>
          </div>
        )}
      </div>

      <label className="block max-w-xl">
        <span className="block text-erp-xs text-muted-foreground mb-1">Contest</span>
        <select value={nominationId ?? ''} className="erp-input h-9 w-full text-erp-sm"
          onChange={(e) => setParams((p) => {
            if (e.target.value) p.set('nomination', e.target.value)
            else p.delete('nomination')
            return p
          }, { replace: true })}>
          <option value="">Choose a contest…</option>
          {contests.map((c) => (
            <option key={c.id} value={c.id}>
              {c.title} · {seatLabel(c.seatTypeCode)} · {c.partyShortName ?? ''}
            </option>
          ))}
        </select>
      </label>

      <ErrorStrip message={error} className="rounded-md border" />

      {!nominationId && (
        <EmptyState title="Choose a contest"
          hint="Aspirants belong to one contest — a party's primary for one seat at one place." />
      )}

      {nominationId && rows !== null && rows.length === 0 && !error && (
        <EmptyState title="Nobody has applied yet"
          hint="Register the aspirants who have taken out papers. Only CLEARED aspirants
                appear on a member's ballot." />
      )}

      {nominationId && (rows === null || rows.length > 0) && (
        <ErpTable head={(
          <>
            <Th className="w-12 text-right">Pos.</Th>
            <Th>Aspirant</Th>
            <Th>Status</Th>
            <Th className="text-right">Fee</Th>
            <Th>Reference</Th>
            <Th />
          </>
        )}>
          {rows === null ? <LoadingRows colSpan={6} />
            : rows.map((a) => (
              <tr key={a.id}>
                <Td className="text-right tabular-nums text-muted-foreground">
                  {a.ballotPosition ?? '—'}
                </Td>
                <Td>
                  <span className="font-medium">{a.ballotName}</span>
                  {a.rejectionReason && (
                    <span className="block text-erp-xs text-red-700 dark:text-red-400">
                      {a.rejectionReason}
                    </span>
                  )}
                </Td>
                <Td><AspirantStatusChip code={a.statusCode} /></Td>
                <Td className="text-right tabular-nums">
                  {a.feeAmount == null ? '—' : fmtNumber(a.feeAmount)}
                </Td>
                <Td className="text-erp-xs text-muted-foreground">{a.feeReference ?? '—'}</Td>
                <Td>
                  <div className="flex items-center justify-end gap-2">
                    {busyId === a.id && <Loader2 className="h-3 w-3 animate-spin" />}
                    {a.statusCode === 'APPLIED' && (
                      <Act label="Record fee" onClick={() => void act(a.id, 'fee')} />
                    )}
                    {(a.statusCode === 'APPLIED' || a.statusCode === 'FEE_PAID') && (
                      <Act label="Start vetting" onClick={() => void act(a.id, 'vet')} />
                    )}
                    {a.statusCode === 'VETTING' && (
                      <>
                        <Act label="Clear" onClick={() => void act(a.id, 'clear')} />
                        <Act label="Reject" danger onClick={() => void act(a.id, 'reject')} />
                      </>
                    )}
                    {a.statusCode !== 'WITHDRAWN' && a.statusCode !== 'REJECTED' && (
                      <Act label="Withdraw" onClick={() => void act(a.id, 'withdraw')} />
                    )}
                  </div>
                </Td>
              </tr>
            ))}
        </ErpTable>
      )}

      {nominationId && (
        <Note tone="info">
          Only <strong>cleared</strong> aspirants appear on a ballot. Positions are drawn at
          random rather than assigned in application order, and the seed is recorded so the
          draw can be reproduced if it is ever questioned.
        </Note>
      )}

      <RegisterModal open={adding} nominationId={nominationId}
        onClose={() => setAdding(false)} onDone={load} />
    </div>
  )
}

function Act({ label, onClick, danger }: {
  label: string; onClick: () => void; danger?: boolean
}) {
  return (
    <button type="button" onClick={onClick}
      className={cn('text-erp-xs hover:underline',
        danger ? 'text-red-700 dark:text-red-400' : 'text-chocolate')}>
      {label}
    </button>
  )
}

function RegisterModal({ open, nominationId, onClose, onDone }: {
  open: boolean; nominationId: string | null
  onClose: () => void; onDone: () => void | Promise<void>
}) {
  const [candidateId, setCandidateId] = useState<string | null>(null)
  const [ballotName, setBallotName] = useState('')
  const [feeAmount, setFeeAmount] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) return
    setCandidateId(null); setBallotName(''); setFeeAmount(''); setError(null); setBusy(false)
  }, [open])

  const submit = async () => {
    if (!nominationId || !candidateId) return
    setBusy(true)
    try {
      setError(null)
      await registerAspirant(nominationId, {
        candidateId, ballotName: ballotName.trim() || null,
        feeAmount: feeAmount ? Number(feeAmount) : null,
      })
      await onDone()
      onClose()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Register an aspirant" width="md">
      <div className="space-y-3">
        <ErrorStrip message={error} className="rounded-md border" />
        <label className="block">
          <span className="block text-erp-xs text-muted-foreground mb-1">Candidate</span>
          <EntityPicker source="candidate" value={candidateId} onChange={setCandidateId} />
        </label>
        <label className="block">
          <span className="block text-erp-xs text-muted-foreground mb-1">
            Ballot name (blank uses the register's)
          </span>
          <input value={ballotName} onChange={(e) => setBallotName(e.target.value)}
            maxLength={200} className="erp-input h-9 w-full text-erp-sm" />
        </label>
        <label className="block">
          <span className="block text-erp-xs text-muted-foreground mb-1">
            Nomination fee (optional)
          </span>
          <input inputMode="decimal" value={feeAmount}
            onChange={(e) => setFeeAmount(e.target.value.replace(/[^0-9.]/g, ''))}
            className="erp-input h-9 w-full text-erp-sm" />
        </label>
        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="erp-btn h-9 px-3 text-erp-sm">
            Cancel
          </button>
          <button type="button" disabled={busy || !candidateId} onClick={() => void submit()}
            className={cn('erp-btn-primary h-9 px-3 text-erp-sm inline-flex items-center gap-2',
              (busy || !candidateId) && 'opacity-50 cursor-not-allowed')}>
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Register
          </button>
        </div>
      </div>
    </Modal>
  )
}
